import { useCallback, useRef } from 'react';
import type { AppLayoutNavWidthChangeDetails, SideNavCollapseChangeDetails } from '@customerio/pluma-components/react';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useAnalytics } from '@/services/analytics';

export const NAV_COLLAPSED_LOCAL_STORAGE_KEY = 'hydra:is-main-nav-collapsed';

type UseNavCollapseChangeOptions = {
  isDefaultCollapsed: boolean;
  /** False while the collapsed state is a transient override rather than the user's choice. */
  shouldPersist: boolean;
};

/**
 * Persists and tracks the main nav's committed collapse changes. A drag's
 * preview flips are neither: they aren't decisions, and each write is a
 * synchronous setItem broadcast to every other tab.
 */
export function useNavCollapseChange({ isDefaultCollapsed, shouldPersist }: UseNavCollapseChangeOptions) {
  const { track } = useAnalytics();
  const [, setIsNavCollapsed] = useLocalStorage<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);
  const collapsedAtRef = useRef<number | null>(isDefaultCollapsed ? Date.now() : null);
  const sessionExpandsAfterCollapseRef = useRef(0);

  return useCallback(
    (isCollapsed: boolean, details: SideNavCollapseChangeDetails) => {
      if (details.isPreview) {
        return;
      }
      // A transient collapse the user did not ask for must not become the
      // preference every other tab and every embedded app then boots from.
      if (shouldPersist) {
        setIsNavCollapsed(isCollapsed);
      }

      const trigger = details.trigger === 'edge-drag' ? 'drag' : 'button_click';
      if (isCollapsed) {
        collapsedAtRef.current = Date.now();
        void track('leftNav', 'collapsed', {
          Trigger: trigger,
          'Session Nav Opens After Collapse': sessionExpandsAfterCollapseRef.current,
        });
      } else {
        sessionExpandsAfterCollapseRef.current += 1;
        const timeCollapsedMs = collapsedAtRef.current != null ? Date.now() - collapsedAtRef.current : 0;
        collapsedAtRef.current = null;
        void track('leftNav', 'expanded', {
          Trigger: trigger,
          'Time Collapsed Ms': timeCollapsedMs,
        });
      }
    },
    [setIsNavCollapsed, shouldPersist, track],
  );
}

/**
 * Publishes the main nav's width as `--hydra-main-nav-width`, which the
 * embedded apps and Hydra's fixed chrome read. Easing is off mid-drag so they
 * track the pointer.
 */
export function useNavWidthChange() {
  const { setVariable, removeVariable } = useThemeContext();

  // A layout unmounted mid-drag never reports its settled width.
  useMountEffect(() => () => removeVariable(GlobalCSSVariable.MainNavTransitionTime));

  return useCallback(
    (width: number, { isDragging }: AppLayoutNavWidthChangeDetails) => {
      setVariable(GlobalCSSVariable.MainNavWidth, `${width}px`);
      if (isDragging) {
        setVariable(GlobalCSSVariable.MainNavTransitionTime, '0ms');
      } else {
        // Restores the stylesheet's duration, so the snap eases instead of jumping.
        removeVariable(GlobalCSSVariable.MainNavTransitionTime);
      }
    },
    [setVariable, removeVariable],
  );
}
